/**
 * Synchronous resolver for the initial map view on cold load.
 *
 * Priority (per user requirements):
 *   1. In-session state seed (caller passes it) — an existing sessionStorage view wins so
 *      mid-session page navigation never re-centers the map.
 *   2. Local persistent cache (last saved view for this device × page).
 *   3. User's timezone coords from `timezoneList.js`.
 *   4. Sydney fallback.
 *
 * All steps are synchronous so React `useState` initializers can consume the result
 * without waiting for the network.
 */
import { readLocalMapView } from './mapViewStorage';
import { getTimezoneCoords, SYDNEY_FALLBACK } from './timezoneList';

/**
 * @param {'live_tracking'|'geofencing'} page
 * @param {{ center?: {lat:number,lng:number}, zoom?: number }} [inSessionSeed]
 * @param {string} [userTimezone] — IANA zone value (from Settings → Units & Measurement → Time Zone)
 * @returns {{ center: {lat:number,lng:number}, zoom: number, source: 'session'|'local'|'timezone'|'fallback' }}
 */
export const resolveInitialMapView = (page, inSessionSeed, userTimezone) => {
  // 1) In-session seed wins (existing behaviour preserved)
  if (inSessionSeed && inSessionSeed.center && typeof inSessionSeed.center.lat === 'number') {
    return {
      center: inSessionSeed.center,
      zoom: typeof inSessionSeed.zoom === 'number' ? inSessionSeed.zoom : 12,
      source: 'session',
    };
  }

  // 2) Local persistent cache (populated from server on previous mounts)
  const local = readLocalMapView(page);
  if (local) {
    return {
      center: { lat: local.lat, lng: local.lng },
      zoom: typeof local.zoom === 'number' ? local.zoom : 12,
      source: 'local',
    };
  }

  // 3) Timezone-derived coords
  if (userTimezone) {
    const coords = getTimezoneCoords(userTimezone);
    if (coords) {
      return {
        center: { lat: coords.lat, lng: coords.lng },
        zoom: coords.zoom,
        source: 'timezone',
      };
    }
  }

  // 4) Sydney fallback
  return {
    center: { lat: SYDNEY_FALLBACK.lat, lng: SYDNEY_FALLBACK.lng },
    zoom: SYDNEY_FALLBACK.zoom,
    source: 'fallback',
  };
};
